import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { AiChatSidebar } from "../AiChatSidebar/AiChatSidebar";
import { AskAiHeader } from "../AskAiHeader/AskAiHeader";
import type {
  AiChatImageAttachment,
  AiChatMessage,
  AiSuggestion,
} from "../types";
import { useAiChat } from "../../../hooks/useAiChat";
import { useVoiceConversation } from "../../../hooks/useVoiceConversation";
import { useSelectedSites } from "../../../hooks/useSelectedSites";
import { useIsUserSuperUserApi } from "../../../api/access-management-api";
import { useSuggestionsApi } from "../../../api/suggestions-api";
import { getAiAssistantScopePath } from "../../../api/ai-assistant-scope";
import { axiosInstance } from "../../../axios-instance";
import { FinalReportUpload } from "./FinalReportUpload";

export interface AiAssistantConfig {
  title: string;
  placeholder?: string;
  site?: string;
  rootEquipment: string;
  /** Speech API override; text/tools remain on the owning module API. Defaults to /copilot/api on the module API origin. */
  copilotApiBase?: string;
  source?: string;
  /** Page suffix sent to the suggestions endpoint; defaults to `source` when omitted. */
  page?: string;
  allowSiteScope?: boolean;
  allowDocumentAttachments?: boolean;
  captureScreen?: (
    signal: AbortSignal,
    target?: HTMLElement
  ) => Promise<AiChatImageAttachment>;
}

const screenSuggestion: AiSuggestion = {
  id: "explain-screen",
  label: "Explain this screen",
  action: "explain-screen",
};

async function sendChatRequest(
  messages: AiChatMessage[],
  site: string | undefined,
  rootEquipment: string,
  signal: AbortSignal,
  conversationId: string,
  source?: string,
  allowSiteScope = false,
  allowDocumentAttachments = false
): Promise<ReadableStreamDefaultReader<Uint8Array>> {
  const url = `${getAiAssistantScopePath(site, rootEquipment, allowSiteScope)}/conversation`;
  const latestUserMessage = [...messages]
    .reverse()
    .find((message) => message.role === "user");
  const latestPdfMessage = allowDocumentAttachments
    ? [...messages]
        .reverse()
        .find(
          (message) =>
            message.role === "user" &&
            message.attachments?.some(
              (attachment) => attachment.mediaType === "application/pdf"
            )
        )
    : undefined;
  const response = await axiosInstance.post(
    url,
    {
      conversationId,
      messages: messages.map((m) => {
        const attachments = m.attachments?.filter((attachment) =>
          attachment.mediaType === "application/pdf"
            ? allowDocumentAttachments && m === latestPdfMessage
            : m === latestUserMessage
        );
        return {
          role: m.role,
          content: m.content,
          ...(attachments?.length ? { attachments } : {}),
        };
      }),
      streamed: true,
      source,
    },
    { signal, responseType: "stream" }
  );
  return (response.data as ReadableStream<Uint8Array>).getReader();
}

interface AiAssistantContextValue {
  open: () => void;
  openAndSend: (
    text: string,
    attachScreen?: boolean | AiChatImageAttachment[],
    captureTarget?: HTMLElement
  ) => Promise<void>;
  suggestions: AiSuggestion[];
  suggestionsLoading: boolean;
  suggestionsError: boolean;
  retrySuggestions: () => void;
  isBusy: boolean;
}

const AiAssistantContext = createContext<AiAssistantContextValue | null>(null);

export function useAiAssistantContext() {
  return useContext(AiAssistantContext);
}

export interface AiAssistantProviderProps extends AiAssistantConfig {
  children: ReactNode;
}

export function AiAssistantProvider({
  children,
  title,
  placeholder = "Type a question...",
  site,
  rootEquipment,
  copilotApiBase,
  source,
  page = source,
  allowSiteScope = false,
  allowDocumentAttachments = false,
  captureScreen,
}: AiAssistantProviderProps) {
  const { data: isSuperUser, isSuccess } = useIsUserSuperUserApi();
  const assistantEnabled = isSuccess && isSuperUser === true;
  const [isOpen, setIsOpen] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [captureError, setCaptureError] = useState<{
    message: string;
    text: string;
    target?: HTMLElement;
  } | null>(null);
  const conversationId = useRef<string | null>(null);
  const captureController = useRef<AbortController | null>(null);
  const { selectedSite: routeSite } = useSelectedSites();
  const selectedSite = site ?? routeSite;
  const voiceApiBase =
    copilotApiBase ??
    new URL(
      "/copilot/api",
      new URL(
        axiosInstance.defaults?.baseURL || "/",
        globalThis.location?.href || "http://localhost"
      )
    ).toString();
  const chatActions = useRef({
    interrupt: () => {},
    utterance: (_text: string) => {},
  });
  const voiceEnabled =
    assistantEnabled &&
    isOpen &&
    !!selectedSite &&
    (!!rootEquipment || allowSiteScope);
  const voice = useVoiceConversation(
    voiceEnabled ? selectedSite : undefined,
    JSON.stringify([
      selectedSite,
      rootEquipment,
      source,
      page,
      allowSiteScope,
      assistantEnabled,
      isOpen,
    ]),
    {
      interrupt: () => chatActions.current.interrupt(),
      utterance: (text) => chatActions.current.utterance(text),
    },
    voiceApiBase
  );
  const {
    data: backendSuggestions,
    isLoading: suggestionsLoading,
    isError: suggestionsError,
    refetch: retrySuggestions,
  } = useSuggestionsApi(
    selectedSite,
    rootEquipment,
    page,
    assistantEnabled,
    allowSiteScope
  );

  const {
    messages,
    isLoading,
    showLoadingMessage,
    sendMessage,
    stopGenerating,
    clearMessages,
  } = useAiChat({
    onResponseStart: voice.beginResponse,
    onAssistantText: voice.append,
    onProgress: voice.reportProgress,
    onResponseEnd: voice.finish,
    onInterrupt: voice.interrupt,
    sendRequest: (msgs, signal) => {
      conversationId.current ??= crypto.randomUUID();
      return sendChatRequest(
        msgs,
        selectedSite,
        rootEquipment,
        signal,
        conversationId.current,
        source,
        allowSiteScope,
        allowDocumentAttachments
      );
    },
  });

  // Avoid carrying a transcript from a previous site/equipment/source into a new conversation route.
  useEffect(() => {
    captureController.current?.abort();
    captureController.current = null;
    setIsCapturing(false);
    setCaptureError(null);
    stopGenerating();
    clearMessages();
    conversationId.current = null;
    setIsOpen(false);
    return () => {
      captureController.current?.abort();
      captureController.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedSite,
    rootEquipment,
    source,
    page,
    allowSiteScope,
    allowDocumentAttachments,
    assistantEnabled,
  ]);

  const openAndSend = async (
    text: string,
    attachScreen: boolean | AiChatImageAttachment[] = false,
    captureTarget?: HTMLElement
  ) => {
    if (isLoading || captureController.current) return;
    setCaptureError(null);
    setIsOpen(true);
    if (Array.isArray(attachScreen)) {
      void sendMessage(text, attachScreen);
      return;
    }
    if (!attachScreen || !captureScreen) {
      void sendMessage(text);
      return;
    }
    const controller = new AbortController();
    captureController.current = controller;
    setIsCapturing(true);
    try {
      const image = captureTarget
        ? await captureScreen(controller.signal, captureTarget)
        : await captureScreen(controller.signal);
      if (controller.signal.aborted || captureController.current !== controller)
        return;
      void sendMessage(text, [image]);
    } catch (error: unknown) {
      if (
        !controller.signal.aborted &&
        captureController.current === controller
      ) {
        setCaptureError({
          message:
            error instanceof Error
              ? error.message
              : "Unable to capture this screen.",
          text,
          target: captureTarget,
        });
      }
    } finally {
      if (captureController.current === controller) {
        captureController.current = null;
        setIsCapturing(false);
      }
    }
  };

  const stopResponse = () => {
    captureController.current?.abort();
    captureController.current = null;
    setIsCapturing(false);
    stopGenerating();
  };
  chatActions.current = {
    interrupt: stopResponse,
    utterance: (text) => {
      if (!captureController.current) void sendMessage(text);
    },
  };

  const suggestions = captureScreen
    ? [
        backendSuggestions?.find(
          (suggestion) => suggestion.action === "explain-screen"
        ) ?? screenSuggestion,
        ...(backendSuggestions ?? [])
          .filter((suggestion) => suggestion.action !== "explain-screen")
          .slice(0, 2),
      ]
    : (backendSuggestions ?? []);

  if (!assistantEnabled) {
    return (
      <AiAssistantContext.Provider value={null}>
        {children}
      </AiAssistantContext.Provider>
    );
  }

  return (
    <AiAssistantContext.Provider
      value={{
        open: () => setIsOpen(true),
        openAndSend,
        suggestions,
        suggestionsLoading,
        suggestionsError,
        retrySuggestions: () => {
          void retrySuggestions();
        },
        isBusy: isLoading || isCapturing,
      }}
    >
      {children}
      <AiChatSidebar
        isOpen={isOpen}
        onClose={() => {
          voice.stop();
          setIsOpen(false);
        }}
        title={title}
        messages={messages}
        isLoading={isLoading || isCapturing}
        showLoadingMessage={showLoadingMessage}
        onSendMessage={sendMessage}
        onStopGenerating={stopResponse}
        placeholder={placeholder}
        voice={
          voiceEnabled && selectedSite
            ? { conversation: voice, site: selectedSite, apiBase: voiceApiBase }
            : undefined
        }
        header={
          captureError ? (
            <div role="alert">
              <p>
                Screen capture could not be attached: {captureError.message}
              </p>
              <button
                type="button"
                onClick={() => {
                  void openAndSend(
                    captureError.text,
                    true,
                    captureError.target
                  );
                }}
              >
                Retry screenshot
              </button>
            </div>
          ) : isCapturing ? (
            <p role="status">Capturing screen...</p>
          ) : allowDocumentAttachments && rootEquipment ? (
            <FinalReportUpload
              key={`${selectedSite}:${rootEquipment}:${source}:${page}`}
              disabled={isLoading}
              onReview={(text, attachments) => {
                void sendMessage(text, attachments);
              }}
            />
          ) : undefined
        }
      />
    </AiAssistantContext.Provider>
  );
}

export interface AiAssistantHeaderProps {
  suggestions?: AiSuggestion[];
}

export function AiAssistantHeader({ suggestions }: AiAssistantHeaderProps) {
  const ctx = useContext(AiAssistantContext);
  if (!ctx) return null;

  const activeSuggestions =
    ctx.suggestions.length > 0 ? ctx.suggestions : suggestions;

  return (
    <>
      <AskAiHeader
        suggestions={activeSuggestions ?? []}
        onSuggestionClick={(s) => {
          void ctx.openAndSend(s.label, s.action === "explain-screen");
        }}
        onOpen={ctx.open}
        loading={ctx.suggestionsLoading}
        disabled={ctx.isBusy}
      />
      {ctx.suggestionsError && (
        <p role="alert" data-ai-assistant-ui>
          Suggested questions could not be loaded.{" "}
          <button type="button" onClick={ctx.retrySuggestions}>
            Retry questions
          </button>
        </p>
      )}
    </>
  );
}
